/**
 * Detalle/edición de reporte con tabs:
 *   - Config: nombre, periodo, summary markdown, cover
 *   - Indicadores: KPIs manuales (audiencia, GRP, reach, etc.)
 *   - Anexos: archivos adjuntos al reporte (PDFs, Excel, imágenes)
 *   - Publicación: portal de transparencia + share token
 *   - Runs: archivos auto-generados (xlsx/pdf detallado/resumen)
 */
import { useState, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Settings2, BarChart3, Paperclip, Globe, Play, Plus, Upload, Trash2,
  Pencil, Check, X, Copy, ExternalLink, Download, FileText,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { media, type AssetKind, type IndicatorKind, type ReportAssetOut, type ReportIndicatorOut, type MediaReportOut, type ReportTarget } from "@/lib/api";
import { ReportTargetPicker } from "@/components/feature/media/report-target-picker";

export const Route = createFileRoute("/_auth/media/reports_/$reportId")({
  component: ReportDetail,
});

function ReportDetail() {
  const { reportId } = Route.useParams();
  const id = Number(reportId);
  const report = useQuery({ queryKey: ["media", "report", id], queryFn: () => media.getReport(id) });

  if (report.isLoading) return <Skeleton className="h-64" />;
  if (!report.data) return <div>Reporte no encontrado</div>;

  const r = report.data;

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold">{r.name}</h2>
          <div className="text-xs text-ink-500 mt-1 flex gap-2 items-center">
            <Badge variant="outline">{r.kind}</Badge>
            <Badge variant="outline">{r.format.toUpperCase()}</Badge>
            {r.is_public && <Badge className="bg-emerald-100 text-emerald-700">📢 Publicado</Badge>}
            {r.period_label && <span>· {r.period_label}</span>}
          </div>
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config"><Settings2 size={14} /> Configuración</TabsTrigger>
          <TabsTrigger value="indicators"><BarChart3 size={14} /> Indicadores</TabsTrigger>
          <TabsTrigger value="assets"><Paperclip size={14} /> Anexos</TabsTrigger>
          <TabsTrigger value="publish"><Globe size={14} /> Publicación</TabsTrigger>
          <TabsTrigger value="runs"><Play size={14} /> Archivos auto</TabsTrigger>
        </TabsList>

        <TabsContent value="config"><ConfigTab reportId={id} /></TabsContent>
        <TabsContent value="indicators"><IndicatorsTab reportId={id} /></TabsContent>
        <TabsContent value="assets"><AssetsTab reportId={id} /></TabsContent>
        <TabsContent value="publish"><PublishTab reportId={id} /></TabsContent>
        <TabsContent value="runs"><RunsTab reportId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Config ============

function ConfigTab({ reportId }: { reportId: number }) {
  const qc = useQueryClient();
  const report = useQuery({ queryKey: ["media", "report", reportId], queryFn: () => media.getReport(reportId) });
  const [form, setForm] = useState<{ name: string; period_label: string; summary_markdown: string; cover_image_url: string }>({
    name: "", period_label: "", summary_markdown: "", cover_image_url: "",
  });

  // Sync form al cargar
  if (report.data && form.name === "") {
    setForm({
      name: report.data.name,
      period_label: report.data.period_label ?? "",
      summary_markdown: report.data.summary_markdown ?? "",
      cover_image_url: report.data.cover_image_url ?? "",
    });
  }

  const patch = useMutation({
    mutationFn: () => media.patchReport(reportId, {
      name: form.name,
      period_label: form.period_label || undefined,
      summary_markdown: form.summary_markdown || undefined,
      cover_image_url: form.cover_image_url || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report", reportId] });
      toast.success("Configuración guardada");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!report.data) return <Skeleton className="h-40" />;

  return (
    <Card>
      <CardContent className="py-5 space-y-3">
        <div>
          <Label className="text-xs">Nombre del reporte</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Período (texto libre)</Label>
          <Input value={form.period_label}
            onChange={(e) => setForm({ ...form, period_label: e.target.value })}
            placeholder="Junio - Diciembre 2026" />
        </div>
        <div>
          <Label className="text-xs">URL imagen de portada</Label>
          <Input value={form.cover_image_url}
            onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
            placeholder="https://" />
          {form.cover_image_url && (
            <img src={form.cover_image_url} alt="Cover" className="mt-2 max-h-32 rounded border border-ink-200" />
          )}
        </div>
        <div>
          <Label className="text-xs">Resumen / introducción (Markdown)</Label>
          <Textarea rows={6}
            value={form.summary_markdown}
            onChange={(e) => setForm({ ...form, summary_markdown: e.target.value })}
            placeholder="# Reporte de transparencia&#10;&#10;Este reporte presenta..." />
          <p className="text-[10px] text-ink-400 mt-1">
            Markdown soportado. Se renderiza en el portal público.
          </p>
        </div>
        <Button onClick={() => patch.mutate()} disabled={patch.isPending}>
          {patch.isPending ? "Guardando…" : "Guardar configuración"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ============ Indicators ============

const INDICATOR_KINDS: { value: IndicatorKind; label: string; unit?: string }[] = [
  { value: "audiencia", label: "Audiencia alcanzada", unit: "personas" },
  { value: "rating", label: "Rating promedio", unit: "puntos" },
  { value: "share", label: "Share %", unit: "%" },
  { value: "grp", label: "GRP", unit: "GRP" },
  { value: "reach", label: "Reach %", unit: "%" },
  { value: "frequency", label: "Frecuencia", unit: "veces" },
  { value: "cpm", label: "CPM", unit: "S/." },
  { value: "impresiones", label: "Impresiones", unit: "" },
  { value: "engagement", label: "Engagement", unit: "" },
  { value: "circulacion", label: "Circulación", unit: "ejemplares" },
  { value: "otro", label: "Otro" },
];

function IndicatorsTab({ reportId }: { reportId: number }) {
  const qc = useQueryClient();
  const reportQ = useQuery({ queryKey: ["media", "report", reportId], queryFn: () => media.getReport(reportId) });
  const adsAllQ = useQuery({ queryKey: ["media", "ads", "all"], queryFn: () => media.listAds({ limit: 1000 }) });
  const adCode = (id: number | null) => id != null ? ((adsAllQ.data ?? []).find(a => a.id === id)?.code ?? `#${id}`) : null;
  const list = useQuery({
    queryKey: ["media", "report-indicators", reportId],
    queryFn: () => media.listReportIndicators(reportId),
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [target, setTarget] = useState<ReportTarget>({});
  const [form, setForm] = useState({
    kind: "audiencia" as IndicatorKind,
    code: "", label: "", value_numeric: "", value_text: "",
    unit: "", source: "", measured_at: "", notes: "", is_public: true,
  });

  function reset() {
    setShowForm(false);
    setEditingId(null);
    setTarget({});
    setForm({
      kind: "audiencia", code: "", label: "", value_numeric: "", value_text: "",
      unit: "", source: "", measured_at: "", notes: "", is_public: true,
    });
  }

  function startEdit(i: ReportIndicatorOut) {
    setEditingId(i.id);
    setTarget({
      campaign_id: i.campaign_id ?? undefined,
      ad_id: i.ad_id ?? undefined,
      placement_id: i.placement_id ?? undefined,
    });
    setForm({
      kind: i.kind as IndicatorKind, code: i.code, label: i.label,
      value_numeric: i.value_numeric != null ? String(i.value_numeric) : "",
      value_text: i.value_text ?? "", unit: i.unit ?? "",
      source: i.source ?? "",
      measured_at: i.measured_at ? i.measured_at.slice(0, 10) : "",
      notes: i.notes ?? "", is_public: i.is_public,
    });
    setShowForm(true);
  }

  const create = useMutation({
    mutationFn: () => media.createReportIndicator(reportId, {
      kind: form.kind, code: form.code, label: form.label,
      value_numeric: form.value_numeric ? Number(form.value_numeric) : undefined,
      value_text: form.value_text || undefined,
      unit: form.unit || undefined, source: form.source || undefined,
      measured_at: form.measured_at || undefined,
      notes: form.notes || undefined, is_public: form.is_public,
      campaign_id: target.campaign_id, ad_id: target.ad_id, placement_id: target.placement_id,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report-indicators", reportId] });
      toast.success("Indicador creado"); reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = useMutation({
    mutationFn: () => media.patchReportIndicator(editingId!, {
      kind: form.kind, code: form.code, label: form.label,
      value_numeric: form.value_numeric ? Number(form.value_numeric) : undefined,
      value_text: form.value_text || undefined,
      unit: form.unit || undefined, source: form.source || undefined,
      measured_at: form.measured_at || undefined,
      notes: form.notes || undefined, is_public: form.is_public,
      campaign_id: target.campaign_id ?? null, ad_id: target.ad_id ?? null, placement_id: target.placement_id ?? null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report-indicators", reportId] });
      toast.success("Indicador actualizado"); reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => media.deleteReportIndicator(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report-indicators", reportId] });
      toast.success("Indicador eliminado");
    },
  });

  function autoSetUnit(k: IndicatorKind) {
    const def = INDICATOR_KINDS.find(x => x.value === k);
    if (def && def.unit !== undefined) {
      setForm(f => ({ ...f, kind: k, unit: f.unit || def.unit! }));
    } else {
      setForm(f => ({ ...f, kind: k }));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-ink-500">
          {(list.data ?? []).length} indicadores manuales
        </div>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus size={14} /> Nuevo indicador
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-4 border-brand-200">
          <CardContent className="py-4 space-y-3">
            <div>
              <Label className="text-xs mb-1 block">Vincular a aviso / placement (opcional)</Label>
              <ReportTargetPicker value={target} onChange={setTarget} clientId={reportQ.data?.client_id} />
              <p className="text-[10px] text-ink-400 mt-1">Sin aviso = indicador general del reporte. El portal agrupa por aviso.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => autoSetUnit(v as IndicatorKind)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INDICATOR_KINDS.map(k => (
                      <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Código *</Label>
                <Input value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, "_") })}
                  placeholder="audiencia_total" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Etiqueta visible *</Label>
                <Input value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Audiencia total alcanzada" />
              </div>
              <div>
                <Label className="text-xs">Valor numérico</Label>
                <Input type="number" step="0.0001"
                  value={form.value_numeric}
                  onChange={(e) => setForm({ ...form, value_numeric: e.target.value })}
                  placeholder="350000" />
              </div>
              <div>
                <Label className="text-xs">Unidad</Label>
                <Input value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="personas, %, GRP, S/." />
              </div>
              <div>
                <Label className="text-xs">Valor texto (alternativo)</Label>
                <Input value={form.value_text}
                  onChange={(e) => setForm({ ...form, value_text: e.target.value })}
                  placeholder="Top 3 nacional" />
              </div>
              <div>
                <Label className="text-xs">Fecha medición</Label>
                <Input type="date" value={form.measured_at}
                  onChange={(e) => setForm({ ...form, measured_at: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Fuente</Label>
                <Input value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder="CPI - Estudio nacional 2026 Q3" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Notas</Label>
                <Textarea rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer md:col-span-2">
                <input type="checkbox" checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
                <span className="text-sm">Visible en portal público</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset}><X size={14} /> Cancelar</Button>
              <Button onClick={() => editingId ? patch.mutate() : create.mutate()}
                disabled={!form.code || !form.label || create.isPending || patch.isPending}>
                <Check size={14} /> {editingId ? "Guardar" : "Crear"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {list.isLoading ? <Skeleton className="h-40" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(list.data ?? []).map(i => (
            <Card key={i.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-[10px] uppercase">{i.kind}</Badge>
                    {i.ad_id && <Badge variant="brand" className="text-[10px] font-mono">{adCode(i.ad_id)}</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(i)}>
                      <Pencil size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove.mutate(i.id)}>
                      <Trash2 size={12} className="text-bad" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-ink-500">{i.label}</div>
                <div className="text-2xl font-bold mt-1 text-ink-900 dark:text-white">
                  {i.value_numeric != null
                    ? Number(i.value_numeric).toLocaleString("es-PE")
                    : i.value_text ?? "—"}
                  {i.unit && <span className="text-base text-ink-500 ml-1">{i.unit}</span>}
                </div>
                {i.source && <div className="text-[10px] text-ink-400 mt-1">Fuente: {i.source}</div>}
                {!i.is_public && (
                  <Badge variant="outline" className="text-[10px] mt-2 text-ink-500">No público</Badge>
                )}
              </CardContent>
            </Card>
          ))}
          {!(list.data ?? []).length && (
            <div className="col-span-full text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              No hay indicadores manuales. Crea uno para mostrar KPIs en el portal de transparencia.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Assets ============

const ASSET_KINDS: { value: AssetKind; label: string }[] = [
  { value: "evidencia", label: "Evidencia" },
  { value: "anexo", label: "Anexo" },
  { value: "transparencia", label: "Informe de transparencia" },
  { value: "contrato", label: "Contrato" },
  { value: "factura", label: "Factura" },
  { value: "auditoria", label: "Auditoría externa" },
  { value: "otro", label: "Otro" },
];

function AssetsTab({ reportId }: { reportId: number }) {
  const qc = useQueryClient();
  const reportQ = useQuery({ queryKey: ["media", "report", reportId], queryFn: () => media.getReport(reportId) });
  const adsAllQ = useQuery({ queryKey: ["media", "ads", "all"], queryFn: () => media.listAds({ limit: 1000 }) });
  const adCode = (id: number | null) => id != null ? ((adsAllQ.data ?? []).find(a => a.id === id)?.code ?? `#${id}`) : null;
  const list = useQuery({
    queryKey: ["media", "report-assets", reportId],
    queryFn: () => media.listReportAssets(reportId),
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [target, setTarget] = useState<ReportTarget>({});
  const [uploadMeta, setUploadMeta] = useState({
    kind: "evidencia" as AssetKind,
    title: "", description: "", is_public: true,
  });
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  async function uploadSelected() {
    if (!pendingFile) return;
    setUploading(true);
    try {
      await media.uploadReportAsset(reportId, pendingFile, {
        kind: uploadMeta.kind,
        title: uploadMeta.title || pendingFile.name,
        description: uploadMeta.description || undefined,
        is_public: uploadMeta.is_public,
        campaign_id: target.campaign_id,
        ad_id: target.ad_id,
        placement_id: target.placement_id,
      });
      qc.invalidateQueries({ queryKey: ["media", "report-assets", reportId] });
      toast.success("Anexo subido");
      setPendingFile(null);
      setTarget({});
      setUploadMeta({ kind: "evidencia", title: "", description: "", is_public: true });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setPendingFile(f);
  }

  const remove = useMutation({
    mutationFn: (id: number) => media.deleteReportAsset(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report-assets", reportId] });
      toast.success("Anexo eliminado");
    },
  });

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver ? "border-brand-500 bg-brand-50" : "border-ink-300"
        }`}
      >
        <Upload size={28} className="mx-auto text-ink-400 mb-2" />
        <p className="text-sm font-medium">Arrastra un archivo o</p>
        <input ref={inputRef} type="file" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingFile(f); e.target.value = ""; }} />
        <Button size="sm" variant="outline" className="mt-2" onClick={() => inputRef.current?.click()}>
          Selecciona
        </Button>
        <p className="text-[10px] text-ink-400 mt-2">PDF, Excel, imágenes, videos, etc. Máx 50 MB</p>
      </div>

      {pendingFile && (
        <Card className="border-brand-200">
          <CardContent className="py-4 space-y-3">
            <div className="text-sm font-semibold flex items-center justify-between">
              <span><FileText size={14} className="inline mr-1" /> {pendingFile.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setPendingFile(null)}><X size={14} /></Button>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Vincular a aviso / placement (opcional)</Label>
              <ReportTargetPicker value={target} onChange={setTarget} clientId={reportQ.data?.client_id} />
              <p className="text-[10px] text-ink-400 mt-1">Sin aviso = anexo general del reporte. El portal agrupa por aviso.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={uploadMeta.kind} onValueChange={(v) => setUploadMeta({ ...uploadMeta, kind: v as AssetKind })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSET_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Título</Label>
                <Input value={uploadMeta.title}
                  onChange={(e) => setUploadMeta({ ...uploadMeta, title: e.target.value })}
                  placeholder={pendingFile.name} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descripción</Label>
                <Textarea rows={2} value={uploadMeta.description}
                  onChange={(e) => setUploadMeta({ ...uploadMeta, description: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer md:col-span-2">
                <input type="checkbox" checked={uploadMeta.is_public}
                  onChange={(e) => setUploadMeta({ ...uploadMeta, is_public: e.target.checked })} />
                <span className="text-sm">Visible en portal público</span>
              </label>
            </div>
            <div className="flex justify-end">
              <Button onClick={uploadSelected} disabled={uploading}>
                {uploading ? "Subiendo…" : "Subir"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {list.isLoading ? <Skeleton className="h-40" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(list.data ?? []).map((a: ReportAssetOut) => (
            <Card key={a.id}>
              <CardContent className="py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{a.kind}</Badge>
                      <Badge variant="outline" className="text-[10px] font-mono">{a.file_kind}</Badge>
                      {a.ad_id && <Badge variant="brand" className="text-[10px] font-mono">{adCode(a.ad_id)}</Badge>}
                      {!a.is_public && <Badge variant="outline" className="text-[10px] text-ink-500">Privado</Badge>}
                    </div>
                    <a href={a.file_url} target="_blank" rel="noopener noreferrer"
                       className="font-medium text-sm hover:underline truncate block">
                      {a.title}
                    </a>
                    {a.description && <div className="text-xs text-ink-500 mt-1">{a.description}</div>}
                    <div className="text-[10px] text-ink-400 mt-1">
                      {a.file_name} · {a.file_size ? `${(a.file_size / 1024 / 1024).toFixed(2)} MB` : "—"}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(a.id)}>
                    <Trash2 size={12} className="text-bad" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!(list.data ?? []).length && (
            <div className="col-span-full text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              No hay anexos. Arrastra archivos arriba para subirlos.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Publish ============

function PublishTab({ reportId }: { reportId: number }) {
  const report = useQuery({ queryKey: ["media", "report", reportId], queryFn: () => media.getReport(reportId) });
  if (!report.data) return <Skeleton className="h-40" />;
  const r = report.data;

  return (
    <div className="space-y-4">
      {/* Primario: portal de transparencia POR CLIENTE (enlace único) */}
      {r.client_id ? (
        <ClientPortalSection clientId={r.client_id} reportIsPublic={r.is_public} />
      ) : (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-900/10">
          <CardContent className="py-4 text-sm text-amber-800 dark:text-amber-300">
            Este reporte no tiene un cliente asignado, por lo que no aparecerá en
            ningún portal de transparencia por cliente. Asígnale un cliente en la
            pestaña <strong>Configuración</strong> para incluirlo en el portal único del cliente.
          </CardContent>
        </Card>
      )}

      {/* Secundario: enlace individual de este reporte (opcional) */}
      <ReportShareSection reportId={reportId} report={r} />
    </div>
  );
}

function ClientPortalSection({ clientId, reportIsPublic }: { clientId: number; reportIsPublic: boolean }) {
  const qc = useQueryClient();
  const clientQ = useQuery({ queryKey: ["media", "client", clientId], queryFn: () => media.getClient(clientId) });
  const [copied, setCopied] = useState(false);

  const publish = useMutation({
    mutationFn: () => media.publishClientPortal(clientId, null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "client", clientId] });
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      toast.success("Portal del cliente publicado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!clientQ.data) return <Skeleton className="h-40" />;
  const c = clientQ.data;
  const portalUrl = c.portal_token ? `${window.location.origin}${media.clientPortalUrl(c.portal_token)}` : null;
  const active = c.portal_enabled && portalUrl;

  function copy() {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Card className="border-brand-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Globe size={16} className="text-brand-600" /> Portal de transparencia de {c.name}
        </CardTitle>
        <CardDescription>
          Enlace <strong>único por cliente</strong>. Reúne todos los reportes publicados
          del cliente y el detalle completo de inserciones en un solo portal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {active ? (
          <>
            <div className="p-3 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 text-xs text-emerald-700">
              <div className="font-semibold flex items-center gap-1.5"><Check size={13} /> Portal activo</div>
              <div className="mt-1">
                {reportIsPublic
                  ? "Este reporte está publicado y aparece en el portal del cliente."
                  : "Publica este reporte (abajo) para que aparezca en el portal del cliente."}
              </div>
            </div>
            <div>
              <Label className="text-xs">Enlace del portal del cliente</Label>
              <div className="flex gap-2 mt-1">
                <Input value={portalUrl} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={copy}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href={portalUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /></a>
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-ink-500">
              El portal de transparencia de este cliente aún no está publicado.
              Publícalo para obtener el enlace único que reúne todos sus reportes.
            </p>
            <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
              <Globe size={14} /> {publish.isPending ? "Publicando…" : "Publicar portal del cliente"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ReportShareSection({ reportId, report: r }: { reportId: number; report: MediaReportOut }) {
  const qc = useQueryClient();
  const [expiresDays, setExpiresDays] = useState(90);
  const [copied, setCopied] = useState(false);

  const publish = useMutation({
    mutationFn: () => media.publishReport(reportId, expiresDays),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report", reportId] });
      toast.success("Reporte publicado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unpublish = useMutation({
    mutationFn: () => media.unpublishReport(reportId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "report", reportId] });
      toast.success("Reporte despublicado");
    },
  });

  const portalUrl = r.share_token ? `${window.location.origin}/public/reports/${r.share_token}` : null;

  function copy() {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Enlace individual de este reporte (opcional)</CardTitle>
        <CardDescription className="text-xs">
          Genera además un enlace público dedicado solo a este reporte. Útil para
          compartir un informe puntual fuera del portal del cliente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {r.is_public && portalUrl ? (
          <>
            <div>
              <Label className="text-xs">URL del reporte individual</Label>
              <div className="flex gap-2 mt-1">
                <Input value={portalUrl} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={copy}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href={portalUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /></a>
                </Button>
              </div>
              <p className="text-[10px] text-ink-400 mt-1">
                Expira: {r.share_expires_at ? new Date(r.share_expires_at).toLocaleDateString("es-PE") : "—"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => unpublish.mutate()} disabled={unpublish.isPending}>
              Despublicar reporte
            </Button>
          </>
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Vigencia (días)</Label>
              <Input type="number" min={1} max={365}
                value={expiresDays} onChange={(e) => setExpiresDays(Number(e.target.value))} />
            </div>
            <Button variant="outline" onClick={() => publish.mutate()} disabled={publish.isPending}>
              <Globe size={14} /> Publicar reporte
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ Runs (archivos auto-generados) ============

function RunsTab({ reportId }: { reportId: number }) {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () => media.runReport(reportId),
    onSuccess: (r) => {
      toast.success(`Encolado run #${r.id}`);
      qc.invalidateQueries({ queryKey: ["media", "report", reportId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardContent className="py-5 space-y-3">
        <p className="text-sm text-ink-500">
          Genera un archivo auto-construido (Excel detallado o PDF resumen)
          desde los placements en DB que matchean los filtros del reporte.
        </p>
        <Button onClick={() => run.mutate()} disabled={run.isPending}>
          <Play size={14} /> {run.isPending ? "Encolando…" : "Ejecutar nuevo run"}
        </Button>
      </CardContent>
    </Card>
  );
}
